import * as THREE from 'three';
import {AbstractDirectionalGuidePointSnapStrategy} from './AbstractDirectionalGuidePointSnapStrategy';
import {PointSnapStrategyType} from './PointSnapStrategyType';
import type {PointSnapRequest} from './PointSnapTypes';
import {isSketchLineObject} from '../../mesh/guards/isSketchLineObject';
import type {SketchLineObject} from '../../mesh/sketch/SketchLineObject';
import {POINT_SNAP_GUIDE_DARK_ORANGE_COLOR} from '../../material';



/**
 * 平行捕捉策略。
 *
 * 设计说明：
 * 1. 公共的方向引导线生命周期由 {@link AbstractDirectionalGuidePointSnapStrategy} 管理；
 * 2. 本类只负责从统一 hover 链路中接收正式草图直线，并把参考直线方向转换为平行引导方向；
 * 3. 最终吸附点仍由公共父类按鼠标到引导线的屏幕距离求得。
 */
export class ParallelPointSnapStrategy extends AbstractDirectionalGuidePointSnapStrategy<SketchLineObject> {
    /** 策略类型。 */
    public readonly strategyType: PointSnapStrategyType = PointSnapStrategyType.Parallel;

    /** 平行捕捉屏幕像素阈值。 */
    protected readonly guideScreenDistanceThresholdPx = 5;

    /** 平行捕捉结果权重。 */
    protected readonly guideSnapWeight = 800;

    /** 平行预览线普通状态颜色。 */
    protected readonly guidePreviewLineColor: THREE.ColorRepresentation = POINT_SNAP_GUIDE_DARK_ORANGE_COLOR;

    /** 平行预览线高亮状态颜色。 */
    protected readonly guideHighlightLineColor: THREE.ColorRepresentation = POINT_SNAP_GUIDE_DARK_ORANGE_COLOR;

    /** 平行预览线屏幕空间虚线段长度。 */
    protected readonly guideScreenSpaceDashSizePx = 4;

    /** 平行预览线屏幕空间虚线间隔长度。 */
    protected readonly guideScreenSpaceGapSizePx = 3;

    /** 普通状态材质名称。 */
    protected readonly guidePreviewMaterialName = '平行预览-屏幕空间虚线材质';

    /** 高亮状态材质名称。 */
    protected readonly guideHighlightMaterialName = '平行高亮-屏幕空间虚线材质';

    /**
     * 解析当前可作为平行参考的源直线。
     *
     * @param request 点捕捉请求。
     * @returns 当前活跃草图中的正式直线；无合法直线时返回 null。
     */
    protected resolveGuideTarget(request: PointSnapRequest): SketchLineObject | null {
        return this.resolveHoveredActiveSketchEdge(
            request,
            (object): object is SketchLineObject => isSketchLineObject(object),
            (target) => target.isDrawingPreviewLine,
        );
    }

    /**
     * 根据参考直线生成平行引导方向。
     *
     * @param firstPoint 绘制起点。
     * @param target 参考直线。
     * @returns 平行引导方向数组。
     */
    protected createGuideDirections(firstPoint: THREE.Vector3, target: SketchLineObject): THREE.Vector3[] {
        void firstPoint;
        return [
            new THREE.Vector3().subVectors(
                target.endPoint.getPosition(),
                target.startPoint.getPosition(),
            ),
        ];
    }
}


